'use client';

import { FunctionComponent, ReactNode } from 'react';

import CpAvatar from '@justgiving/carepack-avatar';
import { DefaultAvatar } from '../DefaultAvatar/DefaultAvatar';
import { Timestamp } from '../Timestamp/Timestamp';
import classNames from 'classnames';
import { formatCurrency } from '../../utils/formatCurrency';
import { i18nInstance } from '../../i18n/client';
import styles from './SupporterDetails.module.scss';
import { useTranslation } from 'react-i18next';

type SupporterDetailsProps = {
  image?: string;
  date?: string;
  donationAmount?: number | null;
  currencyCode?: string;
  giftAidAmount?: number;
  userName?: ReactNode | null;
  message?: ReactNode | null;
};

export const SupporterDetails: FunctionComponent<SupporterDetailsProps> = ({
  image,
  date,
  donationAmount,
  currencyCode,
  giftAidAmount,
  userName,
  message,
}) => {
  const { t } = useTranslation();

  return (
    <li className={styles.container} data-testid="supporter">
      <div className={styles.avatar} data-testid="avatar">
        {image ? (
          <CpAvatar src={image} alt="" size="small" />
        ) : (
          <DefaultAvatar size="small">
            {typeof userName === 'string' ? userName[0] : 'JG'}
          </DefaultAvatar>
        )}
      </div>
      <div className={styles.content}>
        <header className={styles.header}>
          <h2
            className={classNames(
              styles.donorName,
              'cp-heading-extra-small',
              'm-0',
            )}
            data-testid="donor-name"
          >
            {userName || t('common.anonymous')}
          </h2>
          {date && (
            <span
              className={classNames(
                styles.date,
                'cp-body-small',
                'text-color-grey-300',
              )}
            >
              <Timestamp date={date} />
            </span>
          )}
        </header>

        <p className={styles.donationMessage}>{message}</p>
        <DonationAmounts
          donationAmount={donationAmount !== null ? donationAmount : undefined}
          currencyCode={currencyCode}
          giftAidAmount={giftAidAmount}
        />
      </div>
    </li>
  );
};

type DonationAmountsProps = {
  donationAmount?: number;
  currencyCode?: string;
  giftAidAmount?: number;
};

const DonationAmounts: FunctionComponent<DonationAmountsProps> = ({
  donationAmount,
  currencyCode,
  giftAidAmount,
}) => {
  const { t } = useTranslation();

  if (!donationAmount || donationAmount <= 0 || !currencyCode) {
    return null;
  }

  return (
    <div
      className={classNames(styles.amount, 'branded-text')}
      data-testid="donation-amounts"
    >
      {formatCurrency(donationAmount, currencyCode, i18nInstance.language)}
      {typeof giftAidAmount === 'number' && giftAidAmount > 0 && (
        <span className={styles.giftAidAmount}>
          {` + `}
          {formatCurrency(
            giftAidAmount,
            currencyCode,
            i18nInstance.language,
          )}{' '}
          {t('giftAid')}
        </span>
      )}
    </div>
  );
};
